/**
 * Pure model for the B2C reschedule day-column slot grid (PLA-878/879).
 *
 * No React, no I/O: opening hours + per-day slot results + paging state in,
 * visible columns / headline / "Empfohlen" pick / hidden-time count out.
 * Every free time is bookable; nothing is dropped, only scrolled or expanded.
 * The month calendar lives in rescheduleCalendarModel.
 */
import dayjs, { type Dayjs } from "dayjs"

import "dayjs/locale/de"

import type {
  B2cOpeningHoursResponse,
  OptimalBookingTimesResponse,
} from "@/services/firebase/functions"

export type OpeningHoursData = B2cOpeningHoursResponse["openingHours"]
export type ServiceType = "pickup_delivery" | "driveby"

/** Times shown in full per column; the rest scroll (peek) or expand via the toggle. */
export const FULL_SLOTS_PER_DAY = 4
/** Modal content width from which 5 columns fit. */
export const WIDE_LAYOUT_MIN_WIDTH = 400
/** How far ahead open days are enumerated. */
export const HORIZON_DAYS = 60
/** pickup_delivery return trip after the pickup time. */
export const RETURN_OFFSET_HOURS = 2

const DAY_KEYS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
] as const

export type DaySlots = { times: string[]; recommended: string | null }
/** `undefined` = not requested yet, "loading" in flight, "error" failed. */
export type DayResult = DaySlots | "loading" | "error" | undefined

export interface SlotEntry {
  time: string
  /** The customer's current appointment (move intent): shown, never bookable. */
  mine: boolean
}

export interface SlotColumn {
  date: string
  weekday: string
  dayNumber: string
  month: string
  /** Header label: "Heute" / "Morgen" / short weekday. */
  headerLabel: string
  status: "loading" | "error" | "empty" | "ready"
  /** Every time of the day in order, own appointment included. */
  entries: SlotEntry[]
  /** Bookable times only. */
  allTimes: string[]
  recommended: string | null
  /** Gap of more than one calendar day to the previous column (Fr -> Mo). */
  weekBreak: boolean
  /** Entries beyond the four that fit without scrolling. */
  hiddenCount: number
}

export interface SlotGridView {
  columns: SlotColumn[]
  headline: string | null
  recommended: { date: string; time: string } | null
  canPrev: boolean
  canNext: boolean
  /** Sum of hidden times over the visible columns (toggle label). */
  hiddenTotal: number
  /** True when any visible column has more than four entries. */
  hasOverflow: boolean
}

export const fmt = (d: Dayjs, f: string): string => d.locale("de").format(f)

export function columnCountForWidth(width: number | null): 3 | 5 {
  return width !== null && width >= WIDE_LAYOUT_MIN_WIDTH ? 5 : 3
}

/** Free slots as the old picker derived them: feasible `allSlots`, sorted. */
export function deriveDaySlots(resp: OptimalBookingTimesResponse | null | undefined): DaySlots {
  const feasible = (resp?.allSlots ?? []).filter((s) => s.isFeasible).map((s) => s.time)
  const times = Array.from(new Set(feasible)).sort((a, b) => a.localeCompare(b))
  const picked = resp?.recommendedTime ?? null
  return { times, recommended: picked && times.includes(picked) ? picked : null }
}

export function openingEntry(hours: OpeningHoursData | null | undefined, date: string) {
  return hours?.specialDates?.[date] ?? hours?.schedule?.[DAY_KEYS[dayjs(date).day()]]
}

/** Open dates (YYYY-MM-DD) from today on; closed days and a finished today are skipped. */
export function listOpenDates(
  hours: OpeningHoursData | null | undefined,
  now: Dayjs,
  horizon = HORIZON_DAYS,
): string[] {
  if (!hours) return []
  const out: string[] = []
  for (let i = 0; i < horizon; i++) {
    const key = now.add(i, "day").format("YYYY-MM-DD")
    const entry = openingEntry(hours, key)
    if (!entry?.isOpen) continue
    if (i === 0 && entry.close && now.format("HH:mm") >= entry.close) continue
    out.push(key)
  }
  return out
}

/** `currentTime` argument for the per-day callable (today: now, else opening time). */
export function slotQueryTime(
  date: string,
  hours: OpeningHoursData | null | undefined,
  now: Dayjs,
): string {
  if (dayjs(date).isSame(now, "day")) return now.format("HH:mm")
  return openingEntry(hours, date)?.open || "08:00"
}

function headerLabelFor(date: Dayjs, now: Dayjs): string {
  if (date.isSame(now, "day")) return "Heute"
  if (date.isSame(now.add(1, "day"), "day")) return "Morgen"
  return fmt(date, "dd")
}

export interface ColumnOptions {
  /** Times that were rejected as taken in this session; hidden until refetched. */
  taken?: string[]
  /** HH:MM of the customer's current appointment if it lies on this date. */
  mineTime?: string | null
  weekBreak?: boolean
}

function buildEntries(times: string[], mineTime: string | null): SlotEntry[] {
  const free = times.filter((t) => t !== mineTime).map((time) => ({ time, mine: false }))
  if (!mineTime) return free
  return [...free, { time: mineTime, mine: true }].sort((a, b) => a.time.localeCompare(b.time))
}

function buildColumn(
  date: string,
  result: DayResult,
  now: Dayjs,
  { taken = [], mineTime = null, weekBreak = false }: ColumnOptions = {},
): SlotColumn {
  const d = dayjs(date)
  const base = {
    date,
    weekday: fmt(d, "dd"),
    dayNumber: fmt(d, "D"),
    month: fmt(d, "MMM"),
    headerLabel: headerLabelFor(d, now),
    entries: [] as SlotEntry[],
    allTimes: [] as string[],
    recommended: null as string | null,
    weekBreak,
    hiddenCount: 0,
  }
  if (result === undefined || result === "loading") return { ...base, status: "loading" }
  if (result === "error") return { ...base, status: "error" }
  const times = result.times.filter((t) => !taken.includes(t))
  const entries = buildEntries(times, mineTime)
  if (entries.length === 0) return { ...base, status: "empty" }
  const free = entries.filter((e) => !e.mine).map((e) => e.time)
  return {
    ...base,
    status: "ready",
    entries,
    allTimes: free,
    recommended:
      result.recommended && free.includes(result.recommended) ? result.recommended : null,
    hiddenCount: Math.max(0, entries.length - FULL_SLOTS_PER_DAY),
  }
}

export const LOADING_HEADLINE = "Freie Termine werden geladen…"
export const NO_SLOTS_HEADLINE = "Aktuell keine freien Termine"

export function buildHeadline(columns: SlotColumn[], now: Dayjs): string | null {
  const first = columns.find((c) => c.status === "ready" && c.allTimes.length > 0)
  if (!first) {
    if (columns.some((c) => c.status === "loading")) return LOADING_HEADLINE
    if (columns.length > 0 && columns.every((c) => c.status === "empty")) return NO_SLOTS_HEADLINE
    return null
  }
  const time = first.allTimes[0]
  const d = dayjs(first.date)
  if (d.isSame(now, "day")) return `Heute noch frei ab ${time} Uhr`
  if (d.isSame(now.add(1, "day"), "day")) return `Morgen frei ab ${time} Uhr`
  return `Nächster freier Termin: ${first.weekday}, ${first.dayNumber}. ${first.month}, ${time} Uhr`
}

export interface BuildGridInput {
  openDates: string[]
  results: Record<string, DayResult>
  pageStart: number
  columnCount: number
  now: Dayjs
  /** date -> times rejected as taken this session. */
  taken?: Record<string, string[]>
  /** The customer's current appointment (move intent only). */
  mine?: { date: string; time: string } | null
}

export function visibleDates(openDates: string[], pageStart: number, count: number): string[] {
  return openDates.slice(pageStart, pageStart + count)
}

export function buildGrid({
  openDates,
  results,
  pageStart,
  columnCount,
  now,
  taken = {},
  mine = null,
}: BuildGridInput): SlotGridView {
  const dates = visibleDates(openDates, pageStart, columnCount)
  const columns = dates.map((date, i) =>
    buildColumn(date, results[date], now, {
      taken: taken[date],
      mineTime: mine?.date === date ? mine.time : null,
      weekBreak: i > 0 && dayjs(date).diff(dayjs(dates[i - 1]), "day") > 1,
    }),
  )
  const firstFree = columns.find((c) => c.status === "ready" && c.allTimes.length > 0)
  return {
    columns,
    headline: buildHeadline(columns, now),
    recommended: firstFree
      ? { date: firstFree.date, time: firstFree.recommended ?? firstFree.allTimes[0] }
      : null,
    canPrev: pageStart > 0,
    canNext: pageStart + columnCount < openDates.length,
    hiddenTotal: columns.reduce((sum, c) => sum + c.hiddenCount, 0),
    hasOverflow: columns.some((c) => c.hiddenCount > 0),
  }
}

/** Label of the expand toggle. */
export function moreToggleLabel(expanded: boolean, hiddenTotal: number): string {
  return expanded ? "Weniger Uhrzeiten" : `${hiddenTotal} weitere Uhrzeiten`
}

// ─── Confirmation copy ───────────────────────────────────────────────────

export interface ConfirmData {
  tile: { weekday: string; dayNumber: string; month: string }
  fullDate: string
  time: string
  previous: { prefix: string; value: string } | null
  serviceTitle: string
  serviceText: string
  /** Success-screen sentence about what happens next. */
  doneText: string
}

/** Prefix of the struck-through old-appointment line, by picker intent. */
export function previousPrefix(intent?: string): string {
  if (intent === "noshow") return "Verpasst"
  if (intent === "cancelled") return "Storniert"
  return "Bisher"
}

function previousParts(initialDate?: string, initialTime?: string) {
  if (!initialDate) return null
  const d = dayjs(initialDate)
  if (!d.isValid()) return null
  const time = initialTime || (initialDate.includes("T") ? d.format("HH:mm") : null)
  return { day: `${fmt(d, "dd")}, ${fmt(d, "D")}. ${fmt(d, "MMM")}`, time }
}

/** The customer's current appointment as a slot; null unless date and time are known. */
export function currentSlot(
  initialDate?: string,
  initialTime?: string,
): { date: string; time: string } | null {
  const p = previousParts(initialDate, initialTime)
  if (!p?.time) return null
  return { date: dayjs(initialDate).format("YYYY-MM-DD"), time: p.time }
}

/** Old appointment as "Di, 27. Okt. · 09:30 Uhr" (time omitted when unknown). */
export function previousValue(initialDate?: string, initialTime?: string): string | null {
  const p = previousParts(initialDate, initialTime)
  if (!p) return null
  return p.time ? `${p.day} · ${p.time} Uhr` : p.day
}

export function previousAppointmentLabel(
  initialDate?: string,
  initialTime?: string,
  prefix = "Bisher",
): string | null {
  const value = previousValue(initialDate, initialTime)
  return value ? `${prefix}: ${value}` : null
}

export interface ContextBannerData {
  title: string
  body: string
  /** alert = red badge (missed / cancelled / cancel), neutral = clock (move). */
  tone: "alert" | "neutral"
}

/** Alert copy for the no-show / cancelled recovery intents; null otherwise. */
export function buildRecoveryBanner(
  intent: string | undefined,
  initialDate?: string,
  initialTime?: string,
): { title: string; body: string } | null {
  if (intent !== "noshow" && intent !== "cancelled") return null
  const p = previousParts(initialDate, initialTime)
  if (intent === "noshow") {
    const when = p ? ` am ${p.day}${p.time ? ` um ${p.time} Uhr` : ""}` : ""
    return {
      title: `Termin${when} verpasst`,
      body: "Kein Problem: Wählen Sie einfach einen neuen Termin. Ihre Buchungsdaten bleiben erhalten.",
    }
  }
  return {
    title: `Termin${p ? ` am ${p.day}` : ""} wurde storniert`,
    body: "Mit einem neuen Termin nehmen wir Ihre Buchung wieder auf.",
  }
}

/** Context banner for every intent of the pick view; null when there is nothing to say. */
export function buildContextBanner(
  intent: string | undefined,
  initialDate?: string,
  initialTime?: string,
): ContextBannerData | null {
  const recovery = buildRecoveryBanner(intent, initialDate, initialTime)
  if (recovery) return { ...recovery, tone: "alert" }
  if (intent === "cancel") {
    return {
      title: "Wählen Sie einfach einen neuen Termin.",
      body: "Ihre Buchungsdaten bleiben erhalten.",
      tone: "alert",
    }
  }
  const current = previousValue(initialDate, initialTime)
  if (!current) return null
  return {
    title: `Aktueller Termin: ${current}`,
    body: "Wählen Sie unten eine neue Uhrzeit.",
    tone: "neutral",
  }
}

export function buildConfirmData(
  date: string,
  time: string,
  serviceType: ServiceType,
  initialDate?: string,
  initialTime?: string,
  intent?: string,
): ConfirmData {
  const d = dayjs(date)
  const isHbs = serviceType === "pickup_delivery"
  const back = dayjs(`${date}T${time}`).add(RETURN_OFFSET_HOURS, "hour").format("HH:mm")
  const value = previousValue(initialDate, initialTime)
  return {
    tile: { weekday: fmt(d, "dd"), dayNumber: fmt(d, "D"), month: fmt(d, "MMM") },
    fullDate: fmt(d, "dddd, D. MMMM"),
    time,
    previous: value ? { prefix: previousPrefix(intent), value } : null,
    serviceTitle: isHbs ? "Hol- & Bringservice" : "Vorfahrt",
    serviceText: isHbs
      ? `Abholung ab ${time} Uhr · Rückgabe ca. ${back} Uhr`
      : `Sie bringen Ihr Fahrzeug um ${time} Uhr zur Prüfstelle.`,
    doneText: `${
      isHbs
        ? `Wir holen Ihr Fahrzeug ab ${time} Uhr ab.`
        : `Bitte kommen Sie um ${time} Uhr zur Prüfstelle.`
    } Diese Seite zeigt den neuen Termin in wenigen Sekunden.`,
  }
}
